import { useEffect, useId, useRef, useState } from "react";
import { X } from "lucide-react";
import { api, type Kontakt } from "../api";

/** Grobe Prüfung — genug, um Tippfehler wie „anna@firma" oder „anna firma.de" zu fangen. */
export function istGueltigeAdresse(a: string): boolean {
  return /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]{2,}$/.test(a);
}

/** „Anna <anna@firma.de>" → „anna@firma.de"; mehrere durch Komma, Semikolon oder Zeilenumbruch getrennt. */
function zerlegen(text: string): string[] {
  return text
    .split(/[,;\n]+/)
    .map((t) => t.trim())
    .map((t) => (/<([^>]+)>/.exec(t)?.[1] ?? t).trim().replace(/^mailto:/i, "").toLowerCase())
    .filter(Boolean);
}

/**
 * Adressen als Pillen statt als Komma-Liste. Eine falsch gesetzte Kommastelle
 * kostet sonst eine Fehlzustellung, und beim Tippen sieht man nicht, was
 * bereits erkannt wurde. Ungültige Adressen bleiben stehen, sind aber rot
 * markiert — der Composer lässt sie nicht hinaus.
 */
export function AdressFeld({
  label,
  werte,
  onChange,
  autoFocus,
  inputRef,
}: {
  label: string;
  werte: string[];
  onChange: (v: string[]) => void;
  autoFocus?: boolean;
  /** Von außen fokussierbar, z. B. wenn beim Senden ein Empfänger fehlt. */
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
}) {
  const [eingabe, setEingabe] = useState("");
  const [vorschlaege, setVorschlaege] = useState<Kontakt[]>([]);
  const [markiert, setMarkiert] = useState(0);
  const feld = useRef<HTMLInputElement | null>(null);
  const id = useId();
  const listId = `${id}-liste`;

  useEffect(() => {
    const suche = eingabe.trim();
    if (suche.length < 2) {
      setVorschlaege([]);
      return;
    }
    let abgebrochen = false;
    // Kurz warten: Sonst fragt jeder Tastendruck den Server.
    const t = setTimeout(() => {
      void api
        .kontakte(suche)
        .then((k) => {
          if (abgebrochen) return;
          setVorschlaege(k.filter((x) => !werte.includes(x.email.toLowerCase())).slice(0, 8));
          setMarkiert(0);
        })
        .catch(() => !abgebrochen && setVorschlaege([]));
    }, 180);
    return () => { abgebrochen = true; clearTimeout(t); };
  }, [eingabe, werte]);

  const hinzu = (text: string) => {
    const neu = zerlegen(text).filter((a, i, alle) => !werte.includes(a) && alle.indexOf(a) === i);
    if (neu.length > 0) onChange([...werte, ...neu]);
    setEingabe("");
    setVorschlaege([]);
  };

  const taste = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    const offen = vorschlaege.length > 0;
    if (offen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setMarkiert((m) => (e.key === "ArrowDown" ? (m + 1) % vorschlaege.length : (m - 1 + vorschlaege.length) % vorschlaege.length));
      return;
    }
    if (offen && e.key === "Escape") {
      // Erst die Vorschläge schließen, nicht gleich den ganzen Dialog.
      e.preventDefault();
      e.stopPropagation();
      setVorschlaege([]);
      return;
    }
    // Enter und Tab übernehmen den markierten Vorschlag, Komma und Semikolon
    // schließen die getippte Adresse ab — so wie in jedem Mailprogramm.
    if ((e.key === "Enter" || e.key === "Tab") && offen && vorschlaege[markiert]) {
      e.preventDefault();
      hinzu(vorschlaege[markiert]!.email);
      return;
    }
    if ([",", ";", "Enter", "Tab"].includes(e.key) && eingabe.trim()) {
      e.preventDefault();
      hinzu(eingabe);
      return;
    }
    // Enter im leeren Feld soll kein Formular abschicken.
    if (e.key === "Enter") { e.preventDefault(); return; }
    // Rücktaste im leeren Feld holt die letzte Pille zum Bearbeiten zurück.
    if (e.key === "Backspace" && !eingabe && werte.length > 0) {
      e.preventDefault();
      setEingabe(werte[werte.length - 1]!);
      onChange(werte.slice(0, -1));
    }
  };

  return (
    <div className="adressfeld">
      <label htmlFor={id}>{label}</label>
      <div className="pillen-feld" onClick={() => feld.current?.focus()}>
        {werte.map((w) => {
          const gueltig = istGueltigeAdresse(w);
          return (
            <span
              className="adress-pille"
              key={w}
              data-ungueltig={!gueltig || undefined}
              title={gueltig ? w : `„${w}" ist keine gültige Adresse`}
            >
              {w}
              <button
                type="button"
                className="chip-weg"
                aria-label={`${w} entfernen`}
                onClick={(e) => { e.stopPropagation(); onChange(werte.filter((x) => x !== w)); }}
              >
                <X aria-hidden="true" />
              </button>
            </span>
          );
        })}
        <input
          id={id}
          ref={(el) => { feld.current = el; if (inputRef) inputRef.current = el; }}
          autoFocus={autoFocus}
          value={eingabe}
          type="text"
          inputMode="email"
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={vorschlaege.length > 0}
          aria-controls={vorschlaege.length > 0 ? listId : undefined}
          aria-activedescendant={vorschlaege.length > 0 ? `${listId}-${markiert}` : undefined}
          onChange={(e) => {
            const v = e.target.value;
            // Eingefügte Listen („a@x.de, b@y.de") gleich in Pillen zerlegen.
            if (/[,;\n]/.test(v)) hinzu(v);
            else setEingabe(v);
          }}
          onKeyDown={taste}
          onBlur={() => { if (eingabe.trim()) hinzu(eingabe); else setVorschlaege([]); }}
          placeholder={werte.length === 0 ? "name@firma.de" : ""}
        />

        {vorschlaege.length > 0 && (
          <ul className="vorschlaege" id={listId} role="listbox" aria-label="Vorschläge">
            {vorschlaege.map((k, i) => (
              <li
                key={k.email}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === markiert}
                data-markiert={i === markiert}
                onMouseEnter={() => setMarkiert(i)}
                onMouseDown={(e) => { e.preventDefault(); hinzu(k.email); }}
              >
                <span className="vorschlag-name">{k.name || k.email}</span>
                {k.name && <span className="vorschlag-mail">{k.email}</span>}
                {k.geschrieben && <span className="chip">schon geschrieben</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
